<template>
  <v-container fluid class="advisor-dashboard-container">
    <h1
      class="advisor-dashboard-title"
      :class="{ 'mobile-title': $vuetify.breakpoint.xsOnly }"
    >
      Welcome to your dashboard
    </h1>
    <v-row>
      <v-col cols="12" class="text-right mb-4">
        <v-menu
          offset-y
          :close-on-content-click="false"
          content-class="options-menu"
        >
          <template #activator="{ on, attrs }">
            <v-btn dark large v-bind="attrs" v-on="on">
              <v-icon small>{{ icons.mdiCog }}</v-icon
              >Options
            </v-btn>
          </template>
          <v-list>
            <v-list-item class="pa-0">
              <v-btn
                to="/advisors/add-user"
                depressed
                block
                large
                color="white"
                class="ma-0"
              >
                {{ addUserBtnText }}
              </v-btn>
            </v-list-item>
            <v-list-item v-if="isCompanyAdmin" class="pa-0">
              <v-btn
                depressed
                large
                block
                color="white"
                class="ma-0"
                @click.stop="showCompanyDialog = true"
              >
                Edit company details
              </v-btn>
            </v-list-item>
            <v-list-item v-if="isCompanyAdmin || isAdvisor" class="pa-0">
              <ReferralLink />
            </v-list-item>
            <v-list-item v-if="isSppProvider" class="pa-0">
              <v-btn
                depressed
                large
                block
                color="white"
                class="ma-0"
                @click.stop="showDFASPPLeadDialog = true"
              >
                Create lead
              </v-btn>
            </v-list-item>
          </v-list>
        </v-menu>
        <v-badge
          v-if="canViewLatestCommunications"
          :value="unreadCommunicationsCount > 0"
          :content="unreadBadgeContent"
          color="red"
          overlap
          class="ml-2"
        >
          <v-btn color="primary" large @click="showCommunicationsDialog = true">
            <v-icon small>{{ icons.mdiEmailNewsletter }}</v-icon
            >Latest Communications
          </v-btn>
        </v-badge>
      </v-col>
    </v-row>
    <v-card>
      <v-card-title
        class="required-actions"
        :class="{ 'mobile-required-actions': $vuetify.breakpoint.xsOnly }"
        >Next Required Actions</v-card-title
      >

      <v-card
        class="dashboard-inner-card"
        :class="{ 'mobile-inner-card': $vuetify.breakpoint.xsOnly }"
      >
        <div class="dashboard-icons">
          <div
            class="metric-container"
            :class="{
              'active-metric': activeTab === 'complete_steps',
              'active-metric-pink': activeTab === 'complete_steps',
              'mobile-metric-container': $vuetify.breakpoint.xsOnly,
            }"
            @click="setActiveTab('complete_steps')"
          >
            <div class="icon">
              <div
                class="icon-background-circle metric-pink"
                :class="{ 'active-circle': activeTab === 'complete_steps' }"
              >
                <v-img
                  :src="
                    require(`@/assets/shared/advisor-dashboard/icons/pink.svg`)
                  "
                  alt=""
                  class="icon-image"
                  :class="{ 'colored-icon': activeTab === 'complete_steps' }"
                  contain
                />
              </div>
            </div>
            <div
              class="value metric-pink"
              :class="{ 'active-text': activeTab === 'complete_steps' }"
            >
              {{ counts.complete_steps }}
            </div>
            <div
              class="description"
              :class="{ 'active-text': activeTab === 'complete_steps' }"
            >
              Wills with incomplete steps
            </div>
          </div>
          <div
            class="metric-container"
            :class="{
              'active-metric': activeTab === 'download_wills',
              'active-metric-blue': activeTab === 'download_wills',
              'mobile-metric-container': $vuetify.breakpoint.xsOnly,
            }"
            @click="setActiveTab('download_wills')"
          >
            <div class="icon">
              <div
                class="icon-background-circle metric-blue"
                :class="{ 'active-circle': activeTab === 'download_wills' }"
              >
                <v-img
                  :src="
                    require(`@/assets/shared/advisor-dashboard/icons/blue.svg`)
                  "
                  aspect-ratio="1"
                  alt=""
                  class="icon-image"
                  :class="{ 'colored-icon': activeTab === 'download_wills' }"
                  contain
                />
              </div>
            </div>
            <div
              class="value metric-blue"
              :class="{ 'active-text': activeTab === 'download_wills' }"
            >
              {{ counts.download_wills }}
            </div>
            <div
              class="description"
              :class="{ 'active-text': activeTab === 'download_wills' }"
            >
              Wills to download
            </div>
          </div>
          <div
            class="metric-container"
            :class="{
              'active-metric': activeTab === 'sign_upload_wills',
              'active-metric-orange': activeTab === 'sign_upload_wills',
              'mobile-metric-container': $vuetify.breakpoint.xsOnly,
            }"
            @click="setActiveTab('sign_upload_wills')"
          >
            <div class="icon">
              <div
                class="icon-background-circle metric-orange"
                :class="{ 'active-circle': activeTab === 'sign_upload_wills' }"
              >
                <v-img
                  :src="
                    require(`@/assets/shared/advisor-dashboard/icons/orange.svg`)
                  "
                  aspect-ratio="1"
                  alt=""
                  class="icon-image"
                  :class="{ 'colored-icon': activeTab === 'sign_upload_wills' }"
                  contain
                />
              </div>
            </div>
            <div
              class="value metric-orange"
              :class="{ 'active-text': activeTab === 'sign_upload_wills' }"
            >
              {{ counts.sign_upload_wills }}
            </div>
            <div
              class="description"
              :class="{ 'active-text': activeTab === 'sign_upload_wills' }"
            >
              Wills to sign and upload
            </div>
          </div>
          <div
            class="metric-container"
            :class="{
              'active-metric': activeTab === 'validated_wills',
              'active-metric-green': activeTab === 'validated_wills',
              'mobile-metric-container': $vuetify.breakpoint.xsOnly,
            }"
            @click="setActiveTab('validated_wills')"
          >
            <div class="icon">
              <div
                class="icon-background-circle metric-green"
                :class="{ 'active-circle': activeTab === 'validated_wills' }"
              >
                <v-img
                  :src="
                    require(`@/assets/shared/advisor-dashboard/icons/green.svg`)
                  "
                  aspect-ratio="1"
                  alt=""
                  class="icon-image"
                  :class="{ 'colored-icon': activeTab === 'validated_wills' }"
                  contain
                />
              </div>
            </div>
            <div
              class="value metric-green"
              :class="{ 'active-text': activeTab === 'validated_wills' }"
            >
              {{ counts.validated_wills }}
            </div>
            <div
              class="description"
              :class="{ 'active-text': activeTab === 'validated_wills' }"
            >
              Validated Wills
            </div>
          </div>
        </div>
      </v-card>
      <!-- <div class="summary-container" v-if="$vuetify.breakpoint.smAndDown">
        <h3 class="summary-title">Summary</h3>
        <div class="summary-metric-container d-flex">
          <div class="left-metric-container d-flex flex-column">
            <div class="wills-completed d-flex om-bg-green">
              <div>Wills Completed</div>
              <div>R100 000</div>
            </div>
            <div class="wills-outstanding d-flex om-color-green mt-5">
              <div>Wills Outstanding</div>
              <div>R100 000</div>
            </div>

            <div class="estimated-executor-container d-flex flex-column mt-10">
              <div class="mb-3">
                <span class="summary-heading"
                  >Estimated value of Executor fees</span
                >
              </div>
              <div
                class="d-flex flex-column justify-space-evenly potential-actual-container"
              >
                <div class="potential-container d-flex flex-column">
                  <div class="potential-value om-color-green">R23 000 000</div>
                  <div class="potential-label">Potential</div>
                </div>
                <div class="actual-container d-flex flex-column om-bg-green">
                  <div class="actual-value">R4 060 000</div>
                  <div class="actual-label">
                    Actual based on wills in storage
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="right-metric-container">
            <div class="estimated-executor-container d-flex flex-column">
              <div class="mb-3">
                <span class="summary-heading"
                  >Commission earned on executor fees</span
                >
              </div>
              <div
                class="d-flex flex-column justify-space-evenly potential-actual-container"
              >
                <div class="potential-container d-flex flex-column">
                  <div class="potential-value om-color-green">R27 000 000</div>
                  <div class="potential-label">
                    Potential based on completed wills
                  </div>
                </div>
                <div class="actual-container d-flex flex-column om-bg-green">
                  <div class="actual-value">R8 750</div>
                  <div class="actual-label">Actual based on stored wills</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="summary-container" v-else>
        <h3 class="summary-title">Summary</h3>
        <div class="summary-metric-container d-flex">
          <div class="left-metric-container d-flex flex-column">
            <div class="wills-completed d-flex om-bg-green">
              <div>Wills Completed</div>
              <div>R100 000</div>
            </div>
            <div class="estimated-executor-container d-flex flex-column">
              <div class="mb-3">
                <span>Estimated value of Executor fees</span>
              </div>
              <div
                class="d-flex justify-space-evenly potential-actual-container"
              >
                <div class="potential-container d-flex flex-column">
                  <div class="potential-value om-color-green">R23 000 000</div>
                  <div class="potential-label">Potential</div>
                </div>
                <div class="actual-container d-flex flex-column om-bg-green">
                  <div class="actual-value">R4 060 000</div>
                  <div class="actual-label">
                    Actual based on wills in storage
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="right-metric-container">
            <div class="wills-outstanding d-flex om-color-green">
              <div>Wills Outstanding</div>
              <div>R100 000</div>
            </div>
            <div class="estimated-executor-container d-flex flex-column">
              <div class="mb-3">
                <span>Commission earned on executor fees</span>
              </div>
              <div
                class="d-flex justify-space-evenly potential-actual-container"
              >
                <div class="potential-container d-flex flex-column">
                  <div class="potential-value om-color-green">R27 000 000</div>
                  <div class="potential-label">
                    Potential based on completed wills
                  </div>
                </div>
                <div class="actual-container d-flex flex-column om-bg-green">
                  <div class="actual-value">R8 750</div>
                  <div class="actual-label">Actual based on stored wills</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div> -->
    </v-card>
    <v-card class="advisor-dashbord-datatable-container">
      <AdvisorDatatableOptimized
        v-if="!$vuetify.breakpoint.smAndDown"
        :search-advisor-email="searchAdvisorEmail"
        :active-tab="activeTab"
        @update:active-tab="activeTab = $event"
        @update:counts="counts = $event"
      />
      <AdvisorDatatableMobileOptimized
        v-else
        :search-advisor-email="searchAdvisorEmail"
        :active-tab="activeTab"
        @update:active-tab="activeTab = $event"
        @update:counts="counts = $event"
      />
    </v-card>
    <CompanyDialog v-model="showCompanyDialog" :company-to-update="company" />
    <DFASPPLeadDialog v-model="showDFASPPLeadDialog" />
    <LatestCommunicationsDialog
      v-if="canViewLatestCommunications"
      v-model="showCommunicationsDialog"
      @communication-read="onCommunicationRead"
    />
    <SuccessSnackbar v-model="showSuccess" :message="successMessage" />
  </v-container>
</template>

<script>
  import { mdiCog, mdiEmailNewsletter } from '@mdi/js';
  import AdvisorDatatableOptimized from './AdvisorDatatableOptimized.vue';
  import AdvisorDatatableMobileOptimized from './AdvisorDatatableMobileOptimized.vue';
  import SuccessSnackbar from '../common/SuccessSnackbar.vue';
  import CompanyDialog from '../admin/companies/CompanyDialog.vue';
  import DFASPPLeadDialog from '../admin/DFASPPLeadDialog.vue';
  import LatestCommunicationsDialog from './LatestCommunicationsDialog.vue';
  import ReferralLink from '../admin/ReferralLink.vue';
  import {
    ADVISER_COMMUNICATIONS_UNREAD_COUNT_QUERY,
    HAS_ADVISER_COMMUNICATIONS_QUERY,
  } from '../../constants/graphql/advisors';
  export default {
    name: 'AdvisorDashboardOptimized',
    components: {
      AdvisorDatatableOptimized,
      AdvisorDatatableMobileOptimized,
      SuccessSnackbar,
      CompanyDialog,
      DFASPPLeadDialog,
      LatestCommunicationsDialog,
      ReferralLink,
    },
    props: {
      successMessage: {
        required: false,
        default: null,
        type: String,
      },
    },
    data() {
      return {
        icons: {
          mdiCog,
          mdiEmailNewsletter,
        },
        counts: {
          complete_steps: 0,
          download_wills: 0,
          sign_upload_wills: 0,
          validated_wills: 0,
          in_storage: 0,
        },
        useWhiteIcons: true,
        searchAdvisorEmail: null,
        activeTab: 'list_all',
        loading: true,
        company: {},
        showCompanyDialog: false,
        showSuccess: false,
        searchAdvisorEmail: null,
        showDFASPPLeadDialog: false,
        showCommunicationsDialog: false,
        unreadCommunicationsCount: 0,
        hasCommunications: false,
      };
    },
    apollo: {
      hasCommunications: {
        query: HAS_ADVISER_COMMUNICATIONS_QUERY,
        update: data => !!data.hasAdviserCommunications,
        fetchPolicy: 'cache-and-network',
        skip() {
          return !(this.isAdmin || this.isAdvisor);
        },
        error(error) {
          console.error('hasAdviserCommunications query error:', error);
        },
      },
      unreadCommunicationsCount: {
        query: ADVISER_COMMUNICATIONS_UNREAD_COUNT_QUERY,
        update: data => data.adviserCommunicationsUnreadCount || 0,
        fetchPolicy: 'cache-and-network',
        skip() {
          return !this.canViewLatestCommunications;
        },
        error(error) {
          console.error('adviserCommunicationsUnreadCount query error:', error);
        },
      },
    },
    created() {
      if (this.successMessage) this.showSuccess = true;
    },
    computed: {
      isAdmin() {
        return this.$store.getters.isAdmin;
      },
      canViewLatestCommunications() {
        // Backend answers true only for admins and eligible advisers who
        // actually have communications, so an empty inbox shows no button.
        return this.hasCommunications;
      },
      isCompanyAdmin() {
        return this.$store.getters.isCompanyAdmin;
      },
      isAdvisor() {
        return this.$store.getters.isAdvisor;
      },
      isLeadProvider() {
        return this.$store.getters.isLeadProvider;
      },
      isSppProvider() {
        return this.$store.getters.isSppProvider;
      },
      addUserBtnText() {
        if (this.isAdvisor) {
          return 'Add testator';
        } else {
          return 'Add user';
        }
      },
      unreadBadgeContent() {
        return this.unreadCommunicationsCount > 99
          ? '99+'
          : String(this.unreadCommunicationsCount);
      },
    },
    methods: {
      toggleIconColors() {
        this.useWhiteIcons = !this.useWhiteIcons;
      },
      setActiveTab(tab) {
        this.activeTab = tab;
      },
      onCommunicationRead() {
        this.unreadCommunicationsCount = Math.max(
          0,
          this.unreadCommunicationsCount - 1
        );
      },
    },
  };
</script>

<style lang="scss" scoped>
  .advisor-dashboard-container {
    padding: 72px 30px 72px 30px;

    .advisor-dashboard-title {
      font-weight: 600;
      font-size: 40px;
      line-height: 150%;
      &.mobile-title {
        font-weight: 700;
        font-size: 32px;
        line-height: 38px;
        letter-spacing: 0%;
      }
    }

    .required-actions {
      font-weight: 600;
      font-size: 16px;
      line-height: 19px;
      letter-spacing: 0px;
      vertical-align: middle;
      margin-left: 32px;
      &.mobile-required-actions {
        margin-left: 0;
      }
    }
    .dashboard-icons {
      display: flex;
      flex-wrap: wrap;
      height: auto;
      min-height: 160px;

      .metric-container {
        flex: 1 1 calc(25% - 16px);
        min-width: 250px;
        display: flex;
        flex-direction: column;
        gap: 11px;
        cursor: pointer;
        transition: all 0.5s ease;
        padding: 24px 0;

        // Border radius for first and last metric containers (desktop only)
        &:first-child {
          border-bottom-left-radius: 5px;
        }
        &:last-child {
          border-bottom-right-radius: 5px;
        }

        // Hover effects for metric containers
        &:hover {
          // Pink metric hover
          &:nth-child(1) {
            background-color: #ed0080;
            .icon-background-circle {
              background-color: white !important;
            }
            .icon-image {
              filter: none;
            }
            .value {
              color: white !important;
            }
            .description {
              color: white;
            }
          }

          // Blue metric hover
          &:nth-child(2) {
            background-color: #00c0e8;
            .icon-background-circle {
              background-color: white !important;
            }
            .icon-image {
              filter: none;
            }
            .value {
              color: white !important;
            }
            .description {
              color: white;
            }
          }

          // Orange metric hover
          &:nth-child(3) {
            background-color: #f2a025;
            .icon-background-circle {
              background-color: white !important;
            }
            .icon-image {
              filter: none;
            }
            .value {
              color: white !important;
            }
            .description {
              color: white;
            }
          }

          // Green metric hover
          &:nth-child(4) {
            background-color: #00b188;
            .icon-background-circle {
              background-color: white !important;
            }
            .icon-image {
              filter: none;
            }
            .value {
              color: white !important;
            }
            .description {
              color: white;
            }
          }
        }

        &.mobile-metric-container {
          min-width: 50% !important;

          .icon-background-circle {
            width: 48px !important;
            height: 48px !important;
            .icon-image {
              width: 27px !important;
              height: 27px !important;
            }
          }
          .description {
            font-weight: 500;
            font-size: 14px;
            line-height: 20px;
            letter-spacing: 0px;
            text-align: center;
          }
        }
        &.border-right {
          border-right: 1px solid #d1d1d1;
        }
        &.border-bottom {
          border-bottom: 1px solid #d1d1d1;
        }
        &.active-metric-pink {
          background-color: #ed0080;
        }
        &.active-metric-blue {
          background-color: #00c0e8;
        }
        &.active-metric-orange {
          background-color: #f2a025;
        }
        &.active-metric-green {
          background-color: #00b188;
        }

        .icon {
          display: flex;
          justify-content: center;
          align-items: center;
          height: 45%;
          padding: 8px 0;

          .icon-background-circle {
            border-radius: 50%;
            background-color: #f5f5f5;
            width: 64px;
            height: 64px;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.3s ease;

            &.active-circle {
              background-color: white !important;
            }

            &.metric-pink {
              background-color: #ed0080;
            }
            &.metric-blue {
              background-color: #00c0e8;
            }
            &.metric-orange {
              background-color: #f2a025;
            }
            &.metric-green {
              background-color: #00b188;
            }
          }

          .icon-image {
            height: 36px;
            width: 36px;
            max-height: 36px;
            max-width: 36px;
            object-fit: contain;
            filter: brightness(0) invert(1);
            transition: all 0.3s ease;

            &.colored-icon {
              filter: none;
            }
          }
        }

        .value {
          display: flex;
          justify-content: center;
          align-items: center;
          height: 30%;
          font-family: Montserrat;
          font-weight: 700;
          font-size: 36px;
          line-height: 30px;
          letter-spacing: 0px;
          text-align: center;
          transition: all 0.3s ease;

          &.metric-pink {
            color: #ed0080;
          }
          &.metric-blue {
            color: #00c0e8;
          }
          &.metric-orange {
            color: #f2a025;
          }
          &.metric-green {
            color: #00b188;
          }

          &.active-text {
            color: white !important;
          }
        }

        .description {
          display: flex;
          justify-content: center;
          align-items: center;
          height: 25%;
          font-family: Montserrat;
          font-weight: 400;
          font-size: 18px;
          line-height: 24px;
          letter-spacing: 0px;
          text-align: center;
          transition: all 0.3s ease;

          &.active-text {
            color: white;
          }
        }
      }
      .metric-container:nth-child(1),
      .metric-container:nth-child(2),
      .metric-container:nth-child(3) {
        border-right: 1px solid #d1d1d1;
      }
    }
    .border-left {
      border-left: 1px solid #d1d1d1;
    }
    .summary-container {
      padding: 30px 30px 40px 30px;
      .summary-heading {
        font-weight: 500;
        font-size: 16px;
        line-height: 24px;
        letter-spacing: 0px;
      }
      .summary-metric-container {
        flex: 1 1 100%;
        gap: 11px;

        .left-metric-container {
          flex: 1 1 50%;
        }
        .right-metric-container {
          flex: 1 1 50%;
        }
        .wills-completed {
          align-items: center;
          justify-content: space-between;
          padding: 0 20px;
          color: white;
          font-weight: 600;
          font-size: 16px;
          line-height: 19px;
          height: 56px;
          border-radius: 6px;
        }
        .wills-outstanding {
          align-items: center;
          justify-content: space-between;
          padding: 0 20px;
          font-weight: 600;
          font-size: 16px;
          line-height: 19px;
          height: 56px;
          border-radius: 6px;
          background-color: #f5f5f5;
          border: 1px solid #ccc;
        }
      }
      .summary-title {
        font-family: Montserrat;
        font-weight: 600;
        font-size: 22px;
        line-height: 24px;
        letter-spacing: 0px;
        margin-bottom: 24px;
      }
      .estimated-executor-container {
        margin-top: 30px;

        .potential-actual-container {
          gap: 18px;
        }

        .potential-container {
          flex: 1 1 50%;
          height: 79px;
          border-radius: 6px;
          padding: 16px;
          background-color: #f5f5f5;
          border: 1px solid #ccc;
          .potential-value {
            font-weight: 600;
            font-size: 24px;
            line-height: 30px;
            letter-spacing: 0px;
          }
          .potential-label {
            font-weight: 500;
            font-size: 14px;
            line-height: 24px;
            letter-spacing: 0px;
            color: #333;
          }
        }
        .actual-container {
          height: 79px;
          border-radius: 6px;
          padding: 16px;
          .actual-value {
            font-weight: 600;
            font-size: 24px;
            line-height: 30px;
            letter-spacing: 0px;
            color: white;
          }
          .actual-label {
            font-weight: 500;
            font-size: 14px;
            line-height: 24px;
            letter-spacing: 0px;
            color: white;
          }
        }

        .actual-container {
          flex: 1 1 50%;
        }
      }
    }
  }

  .dashboard-inner-card {
    border: none !important;
    box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 1px 5px 0 rgba(0, 0, 0, 0.12) !important;
    border-radius: 0 0 4px 4px !important;
    &.mobile-inner-card {
      padding: 5px;
      box-shadow: none !important;
      border-radius: 0 !important;
    }
  }
  .advisor-dashbord-datatable-container {
    margin-top: 40px;
  }

  @media (max-width: 1270px) {
    .summary-container {
      .summary-metric-container {
        flex-direction: column;
        gap: 18px;
        .left-metric-container,
        .right-metric-container {
          flex: 1 1 100%;
          width: 100%;
        }
      }
    }
    .metric-container {
      min-width: unset;
    }
  }

  @media (max-width: 600px) {
    .dashboard-icons {
      display: flex;
      flex-wrap: wrap;
      flex-direction: row;
      gap: 0;
      .metric-container {
        flex: 0 0 50%;
        max-width: 50%;
        min-width: 50%;
        box-sizing: border-box;
        padding: 10px 0;
        .icon-background-circle {
          width: 40px;
          height: 40px;
        }
        .icon-image {
          width: 22px;
          height: 22px;
        }
        .value {
          font-size: 22px;
        }
        .description {
          font-size: 13px;
        }
      }
      .mobile-metric-container {
        border: 1px solid #d1d1d1;
        border-left: none;
        border-top: none;
      }
      .mobile-metric-container:nth-child(1),
      .mobile-metric-container:nth-child(2) {
        border-top: 1px solid #d1d1d1;
      }
      .mobile-metric-container:nth-child(2) {
        border-right: none !important;
      }
      .mobile-metric-container:nth-child(2n) {
        border-right: none;
      }
      .mobile-metric-container:nth-child(n + 3) {
        border-bottom: 1px solid #d1d1d1;
      }
    }
    .summary-container {
      padding: 8px 0 8px 0;
      .summary-title {
        font-size: 16px;
      }
      .summary-metric-container {
        flex-direction: column;
        gap: 10px;
        .left-metric-container,
        .right-metric-container {
          flex: 1 1 100%;
          width: 100%;
        }
        .wills-completed,
        .wills-outstanding {
          font-size: 12px;
          height: 32px;
          padding: 0 6px;
        }
        .potential-container,
        .actual-container {
          font-size: 14px;
          padding: 4px;
          height: 40px;
        }
      }
    }
    .advisor-dashbord-datatable-container {
      margin-top: 10px;
    }
  }

  // NEW: Force 2-per-row layout for metrics between 780px and 1030px
  @media (min-width: 780px) and (max-width: 1030px) {
    .dashboard-icons {
      .metric-container {
        flex: 0 0 50% !important;
        max-width: 50% !important;
        min-width: 50% !important;
      }
    }
  }

  // Responsive: Remove border radius when wrapping to 2 per row
  @media (max-width: 1030px) {
    .dashboard-icons {
      .metric-container {
        border-bottom-left-radius: 0 !important;
        border-bottom-right-radius: 0 !important;
      }
    }
  }
</style>
